import React, { useState } from 'react';
import styles from './Accordion.module.css';
import { CheckmarkRegular, Copy20Filled } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

type DataSourceLinkListObj = {
  sourceTitle:string;
  sourceLink:string;
}

interface AccordionItemProps {
  title: string;
  content: string;
  sourceSystem: string | undefined | null;
  prompts: string[];
  datasourcelink: string | DataSourceLinkListObj[];
  contact?: string;
  feedback?: boolean;
}

interface DataSourceItem {
  dataSource: string;
  AccordionItemProps: AccordionItemProps[];
}

interface CollapsibleListProps {
  dataSources: DataSourceItem[];
}

const AccordionItem: React.FC<{ item: DataSourceItem }> = ({ item }) => {
  const [isOpenParent, setIsOpenParent] = useState<boolean>(false); // state for parent (dataSource)
  const [isOpenChild, setIsOpenChild] = useState<boolean[]>(new Array(item.AccordionItemProps.length).fill(false)); // state for child (title)
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleParentAccordion = () => {
    setIsOpenParent(!isOpenParent);
  };

  const toggleChildAccordion = (index: number) => {
    const newIsOpenChild = [...isOpenChild];
    newIsOpenChild[index] = !newIsOpenChild[index];
    setIsOpenChild(newIsOpenChild);
  };

  const onCopyMessageClicked = (ques: string, index: number) => {
    navigator.clipboard.writeText(ques).then(() => {
      setActiveIndex(index);
      setTimeout(() => setActiveIndex(null), 2000);
    }).catch(err => console.error('Failed to copy text: ', err));
  };

  const OpenMailClient = (email: string) => {
    window.open(`mailto:${email}`)
  }

  return (
    <div className="accordion-item">
      <h2 className="accordion-header">
        <button
          className={`accordion-button ${isOpenParent ? '' : 'collapsed'}`}
          onClick={toggleParentAccordion}
        >
          {item.dataSource}
        </button>
      </h2>
      <div className={`accordion-collapse collapse ${isOpenParent ? 'show' : ''}`}>
        <div className="accordion-body">
          {item.AccordionItemProps.map((prop, index) => (
            <div key={index}>
              <h2 className="accordion-item accordion-header" style={{borderRadius:"0px"}}>
                <button
                  className={`accordion-button ${isOpenChild[index] ? '' : 'collapsed'}`}
                  onClick={() => toggleChildAccordion(index)}
                >
                  {/* {prop.title} */}
                  {`${index + 1}. ${prop.title}`}
                </button>
              </h2>
              <div className={`accordion-collapse collapse ${isOpenChild[index] ? 'show' : ''}`}>
                <div className="accordion-body">
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{prop.content}</ReactMarkdown>
                  {prop.sourceSystem && (
                    <>
                      <br />
                      <strong>Source systems:</strong>{' '}
                      {prop.sourceSystem.replace(/^Source systems:\s*/i, '')}
                    </>
                  )}
                  {prop.feedback && <div>For any further queries or requests, please click on the <b>Give Us Feedback</b> section.</div>}
                  {prop.prompts.length > 0 && (
                  <div>
                    <p className={styles.prompTitle}>Sample prompts:</p>
                    {prop.prompts.map((item, idx) => (
                      <div style={{ display: 'flex' }} key={idx}>
                        <div className={styles.promptText}>{idx + 1}. {item}</div>
                        {activeIndex === idx ? (
                          <CheckmarkRegular
                            style={{ height: '20px', width: '20px', color: 'green', cursor: 'pointer' }}
                          />
                        ) : (
                          <Copy20Filled
                            onClick={() => onCopyMessageClicked(item, idx)}
                            style={{ color: 'slategray', cursor: 'pointer' }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                  {((typeof prop.datasourcelink === 'string' && prop.datasourcelink.trim().length > 0) || (Array.isArray(prop.datasourcelink) && prop.datasourcelink.length > 0)) && (
                      <div className={styles.datasource} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className={styles.datasourceLinkTitle}>DataSource:</div>
                        <div>
                          {typeof prop.datasourcelink === 'string' ? (
                            <a
                              href={prop.datasourcelink}
                              className={styles.datasourceLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >{prop.title}</a>
                          ) : (
                            prop.datasourcelink.map((source,index)=>(
                              <>
                                <a
                                  key={source.sourceTitle + index}
                                  href={source.sourceLink}
                                  className={styles.datasourceLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >{source.sourceTitle}</a>
                                {index < prop.datasourcelink.length - 1 && ', '}
                              </>
                            ))
                          )}
                        </div>
                      </div>
                  )}
                  {prop.contact && typeof prop.datasourcelink === 'string' && (
                     <div>
                        <div className={styles.datasource} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div className={styles.datasourceLinkTitle}>For any access related requests, please contact:</div>
                          <div>
                            <a
                              href={prop.datasourcelink}
                              className={styles.datasourceLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => OpenMailClient(prop.contact ? prop.contact : '')}
                            >
                              {prop.contact}
                            </a>
                          </div>
                        </div>
                      </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Accordion: React.FC<{ items: DataSourceItem[] }> = ({ items }) => {
  return (
    <div>
      <style>
        {`
          .accordion-button:not(.collapsed) {
            color: var(--bs-accordion-active-color);
            background-color: #f2f2f2;
            box-shadow: inset 0 calc(-1* var(--bs-accordion-border-width)) 0 var(--bs-accordion-border-color);
          }
          .accordion-button:focus {
            z-index: 3;
            outline: 0;
            box-shadow: none;
          }
          .accordion-body {
              border: var(--bs-accordion-border-width) solid var(--bs-accordion-border-color);
          }
        `}
      </style>
      <div className="accordion" id="accordion">
        {items.map((item, index) => (
          <AccordionItem key={index} item={item} />
        ))}
      </div>
    </div>
  );
};

export default Accordion;